import { useEffect, useRef } from "react";

interface PreferredSourceCTAProps {
  /** Short line of context shown above the button */
  headline?: string;
  /** Domain used for the no-JS fallback link */
  domain?: string;
  theme?: "light" | "dark";
  className?: string;
}

/**
 * Renders Google's official "Add as Preferred Source" button.
 * The swg-basic.js library is loaded once in index.html; it upgrades the
 * [google-add-preferred-source-btn] container in place. If the script is
 * blocked, the fallback link to Google's preferences page still works.
 */
const PreferredSourceCTA = ({
  headline = "Getting useful roofing answers here? Add O'Brien Contracting as a preferred source on Google so our guides show up first in Search and AI results.",
  domain = "obriencontractingusa.com",
  theme = "light",
  className = "",
}: PreferredSourceCTAProps) => {
  const holderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Re-run the library on client-side route changes so the container
    // mounted after initial load still gets upgraded.
    const swg = (window as unknown as { SWG?: unknown[] }).SWG;
    if (Array.isArray(swg)) {
      swg.push((subscriptions: { setOnEntitlementsResponse?: unknown }) => {
        void subscriptions;
      });
    }
  }, []);

  const isDark = theme === "dark";

  return (
    <aside
      ref={holderRef}
      className={`rounded-2xl border p-6 sm:p-7 ${
        isDark
          ? "border-accent/40 bg-primary-foreground/[0.07] text-primary-foreground"
          : "border-border bg-muted/40 text-foreground"
      } ${className}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex-1">
          <p className={`text-base font-semibold leading-snug ${isDark ? "text-primary-foreground" : "text-foreground"}`}>
            {headline}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          {/* Google upgrades this container in place when the publisher is eligible */}
          <div {...({ "google-add-preferred-source-btn": "", theme } as Record<string, string>)} />
          {/* Always-visible fallback so the action is never invisible */}
          <a
            href={`https://www.google.com/preferences/source?q=${domain}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              // Google sends X-Frame-Options: SAMEORIGIN, so this must open as a
              // real top-level tab — never inside an embedded/preview frame.
              e.preventDefault();
              window.open(
                `https://www.google.com/preferences/source?q=${domain}`,
                "_blank",
                "noopener,noreferrer"
              );
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-accent px-5 py-3 text-sm font-bold text-accent-foreground shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z"/>
              <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1A12 12 0 0 0 12 24z"/>
              <path fill="#FBBC05" d="M5.4 14.4a7.2 7.2 0 0 1 0-4.6V6.7H1.4a12 12 0 0 0 0 10.8l4-3.1z"/>
              <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0A12 12 0 0 0 1.4 6.7l4 3.1C6.3 6.9 8.9 4.8 12 4.8z"/>
            </svg>
            Add as Preferred Source
          </a>
        </div>
      </div>
    </aside>
  );
};


export default PreferredSourceCTA;
